// Мост между браузерным выбором cookie и реестром согласий.
//
// Два состояния, между которыми легко получить расхождение:
//
//   • **гость** — выбора в реестре нет и быть не может, он живёт в
//     `localStorage`;
//   • **вошедший** — источник истины сервер, `localStorage` остаётся кэшем,
//     по которому гейт аналитики отвечает синхронно, без ожидания запроса.
//
// Правило перехода одно: после входа гостевой выбор **переносится** в реестр
// (append-only запись с `source: guest_import`), а дальше кэш только следует
// за сервером. Обратного направления нет — иначе очистка localStorage тихо
// отзывала бы согласие, записанное в реестр.
import type { CookieCategoryChoices } from "@iec-id/shared/constants";
import type { ConsentSource } from "@iec-id/shared/contracts/consents";
import {
	decideCookieSync,
	markCookieConsentSynced,
	syncCookieConsentFromServer,
	useCookieConsent,
} from "@iec-id/ui";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/trpc";

export type CookieRegistry = {
	/** Действующий выбор: сервер, если он есть, иначе браузер. */
	choices: CookieCategoryChoices | null;
	/** Решение принято (в реестре либо в браузере). */
	decided: boolean;
	/** Выбор пока только в браузере и ждёт переноса в реестр. */
	pendingImport: boolean;
	decidedAt: string | null;
	version: string | null;
	isPending: boolean;
	isSaving: boolean;
	isError: boolean;
	save: (choices: CookieCategoryChoices, source?: ConsentSource) => void;
};

export type CookieRegistryOptions = {
	/** Вызывается после того, как решение реально легло в реестр. */
	readonly onSaved?: (() => void) | undefined;
	readonly onSaveFailed?: (() => void) | undefined;
};

/**
 * Единственная точка чтения и записи выбора cookie в витрине. Оба потребителя
 * (баннер и карточка настроек) ходят сюда — иначе получилось бы два разных
 * представления об одном согласии.
 */
export function useCookieRegistry(options: CookieRegistryOptions = {}): CookieRegistry {
	const { i18n } = useTranslation();
	const utils = api.useUtils();
	const local = useCookieConsent();
	// Ретраев нет: 401 у гостя — не ошибка сети, а нормальный ответ, повторять
	// его незачем.
	const server = api.consents.cookies.useQuery(undefined, { retry: false });

	const documents = api.legal.documents.useQuery({ locale: i18n.language });
	const cookiesDocument = documents.data?.items.find((item) => item.key === "cookies");

	const save = api.consents.setCookies.useMutation({
		onSuccess: (data) => {
			utils.consents.cookies.setData(undefined, data);
			syncCookieConsentFromServer({
				choices: data.choices,
				decidedAt: data.decidedAt ?? new Date().toISOString(),
				version: data.version,
				sha256: data.sha256,
			});
			void utils.consents.history.invalidate();
			options.onSaved?.();
		},
		onError: () => {
			// Кэш браузера НЕ трогаем: сервер выбор не принял, и локально
			// показанное «сохранено» было бы неправдой.
			options.onSaveFailed?.();
		},
	});

	// Перенос выполняется один раз на монтирование: повторная попытка при том
	// же ответе сервера дала бы вторую запись в append-only реестре, то есть
	// дубль решения, которого человек не принимал.
	const imported = useRef(false);
	const saveMutate = save.mutate;

	useEffect(() => {
		const state = server.data;
		// Ответ реестра получен — значит сессия есть: процедура защищённая.
		const decision = decideCookieSync({ local, server: state, authenticated: state !== undefined });

		if (decision.action === "adopt-server" && state) {
			syncCookieConsentFromServer({
				choices: state.choices,
				decidedAt: state.decidedAt ?? new Date().toISOString(),
				version: state.version,
				sha256: state.sha256,
			});
			return;
		}

		if (decision.action !== "import" || imported.current) return;
		imported.current = true;
		// Редакция намеренно НЕ передаётся: гость решал по тому тексту, что был
		// открыт у него в браузере неизвестно когда, и сверка хеша сорвала бы
		// перенос вместо того, чтобы его выполнить. Сервер запишет действующую
		// редакцию, а `source: guest_import` честно скажет, откуда решение.
		saveMutate({ choices: decision.choices, source: "guest_import" });
		markCookieConsentSynced();
	}, [server.data, local, saveMutate]);

	const serverState = server.data;
	const decidedOnServer = serverState?.decided === true;

	return {
		choices: decidedOnServer ? serverState.choices : (local?.choices ?? null),
		decided: decidedOnServer || local !== null,
		pendingImport: !decidedOnServer && local !== null && !local.synced,
		decidedAt: decidedOnServer ? serverState.decidedAt : (local?.decidedAt ?? null),
		version: decidedOnServer ? serverState.version : (local?.version ?? null),
		isPending: server.isPending,
		isSaving: save.isPending,
		isError: server.isError,
		save: (choices, source = "legal_center") =>
			saveMutate({
				choices,
				source,
				locale: i18n.language,
				// Решение, принятое ЗДЕСЬ, делается по видимой редакции — её версия
				// и хеш уезжают вместе с выбором и сверяются сервером.
				...(cookiesDocument
					? { version: cookiesDocument.version, sha256: cookiesDocument.sha256 }
					: {}),
			}),
	};
}
